/**
 * CSV Upload Modal
 * 
 * Allows users to upload CSV files containing government opportunities
 * with automatic template detection, column mapping, and data normalization.
 */

import React, { useState } from 'react';
import { Upload, X, CheckCircle, AlertCircle, ChevronDown, ChevronRight, FileText, Download } from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner } from '@tracekit/core';
import { 
  csvETLService, 
  DATA_SOURCE_TEMPLATES, 
  DataSourceTemplate, 
  ColumnMapping, 
  ETLResult 
} from '../services/csvETLService';

export interface CSVUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: any[], metadata: { source: string; template: string }) => Promise<void>;
  projectId: string;
}

type Step = 'upload' | 'template' | 'mapping' | 'preview' | 'processing' | 'complete';

export function CSVUploadModal({ isOpen, onClose, onUpload, projectId }: CSVUploadModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<DataSourceTemplate | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [etlResult, setEtlResult] = useState<ETLResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [uploadOptions, setUploadOptions] = useState({
    strict: false,
    skipInvalidRows: true,
  });

  // Reset modal state
  const reset = () => {
    setStep('upload');
    setFile(null);
    setCsvData([]);
    setCsvColumns([]);
    setSelectedTemplate(null);
    setColumnMappings([]);
    setEtlResult(null);
    setError(null);
    setShowAdvancedOptions(false);
  };

  // Handle file selection
  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);

    try {
      // Parse CSV
      const { data, columns, errors } = await csvETLService.parseCSV(selectedFile);

      if (errors.length > 0) {
        setError(`CSV parsing errors: ${errors.map(e => e.message).join(', ')}`);
        return;
      }

      setCsvData(data);
      setCsvColumns(columns);

      // Auto-detect template
      const detectedTemplate = csvETLService.detectTemplate(columns);
      setSelectedTemplate(detectedTemplate || DATA_SOURCE_TEMPLATES.generic);
      
      if (detectedTemplate) {
        setColumnMappings([...detectedTemplate.defaultMappings]);
      }

      setStep('template');
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: DataSourceTemplate) => {
    setSelectedTemplate(template);
    setColumnMappings([...template.defaultMappings]);
    setStep('mapping');
  };

  // Handle column mapping changes
  const handleMappingChange = (index: number, field: keyof ColumnMapping, value: any) => {
    const newMappings = [...columnMappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setColumnMappings(newMappings);
  };

  // Add new mapping
  const handleAddMapping = () => {
    setColumnMappings([
      ...columnMappings,
      { csvColumn: '', entityField: '', required: false },
    ]);
  };

  // Remove mapping
  const handleRemoveMapping = (index: number) => {
    const newMappings = [...columnMappings];
    newMappings.splice(index, 1);
    setColumnMappings(newMappings);
  };

  // Preview transformation
  const handlePreview = async () => {
    setError(null);

    try {
      const result = await csvETLService.transformData(
        csvData,
        columnMappings.filter(m => m.csvColumn && m.entityField), // Only include complete mappings
        null, // Entity schema - will be loaded from policy pack
        {
          strict: uploadOptions.strict,
          skipInvalidRows: uploadOptions.skipInvalidRows,
          sourceSystem: selectedTemplate?.id || 'csv_upload',
        }
      );

      setEtlResult(result);
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Failed to transform data');
    }
  };

  // Confirm and upload
  const handleConfirmUpload = async () => {
    if (!etlResult) return;

    setStep('processing');
    setError(null);

    try {
      await onUpload(etlResult.data, {
        source: selectedTemplate?.id || 'csv_upload',
        template: selectedTemplate?.name || 'Generic CSV',
      });

      setStep('complete');
    } catch (err: any) {
      setError(err.message || 'Failed to upload data');
      setStep('preview');
    }
  };

  // Download sample CSV template
  const handleDownloadTemplate = (template: DataSourceTemplate) => {
    const headers = template.defaultMappings.map(m => m.csvColumn);
    const csv = headers.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.id}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Upload Opportunities CSV</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Import opportunities from Merx, BC Bid, SAP Ariba, JAGGAER, or custom CSV
            </p>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {[
              { id: 'upload', label: 'Upload' },
              { id: 'template', label: 'Template' },
              { id: 'mapping', label: 'Mapping' },
              { id: 'preview', label: 'Preview' },
              { id: 'complete', label: 'Complete' },
            ].map((s, index, arr) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step === s.id
                        ? 'bg-blue-600 text-white'
                        : arr.findIndex(x => x.id === step) > index
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {arr.findIndex(x => x.id === step) > index ? <CheckCircle className="h-5 w-5" /> : index + 1}
                  </div>
                  <span className="text-xs mt-1 text-gray-600 dark:text-gray-400">{s.label}</span>
                </div>
                {index < arr.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${arr.findIndex(x => x.id === step) > index ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-red-900 dark:text-red-100">Error</div>
                <div className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</div>
              </div>
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Upload CSV File</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Select a CSV file exported from Merx, BC Bid, SAP Ariba, JAGGAER, or a custom format
                </p>

                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) handleFileSelect(selectedFile);
                  }}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                >
                  <Upload className="h-5 w-5" />
                  Select CSV File
                </label>
              </div>

              {/* Sample templates */}
              <div className="mt-8">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Need a template?</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.values(DATA_SOURCE_TEMPLATES).filter(t => t.id !== 'generic').map((template) => (
                    <Card key={template.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{template.name}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadTemplate(template)}
                          className="ml-2"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Template Selection */}
          {step === 'template' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Select Data Source Template</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {selectedTemplate && selectedTemplate.id !== 'generic'
                    ? `Detected: ${selectedTemplate.name}. Confirm or select a different template.`
                    : 'Choose the data source template that matches your CSV format.'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.values(DATA_SOURCE_TEMPLATES).map((template) => (
                  <Card
                    key={template.id}
                    className={`p-4 cursor-pointer border-2 transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{template.name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description}</div>
                        {template.country && (
                          <Badge variant="default" className="mt-2 text-xs">{template.country}</Badge>
                        )}
                      </div>
                      {selectedTemplate?.id === template.id && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
                <Button onClick={() => handleTemplateSelect(selectedTemplate!)} disabled={!selectedTemplate}>
                  Next: Configure Mapping
                </Button>
              </div>
            </div>
          )}

          {/* Step: Column Mapping */}
          {step === 'mapping' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Map CSV Columns to Entity Fields</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Review and adjust how CSV columns map to opportunity entity fields. 
                  {selectedTemplate && selectedTemplate.id !== 'generic' && ' (Auto-mapped based on template)'}
                </p>
              </div>

              <div className="space-y-3">
                {columnMappings.map((mapping, index) => (
                  <Card key={index} className="p-4">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-5">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                          CSV Column {mapping.required && <span className="text-red-600">*</span>}
                        </label>
                        <select
                          value={mapping.csvColumn}
                          onChange={(e) => handleMappingChange(index, 'csvColumn', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                        >
                          <option value="">-- Select Column --</option>
                          {csvColumns.map((col) => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-1 text-center text-gray-400">
                        →
                      </div>

                      <div className="col-span-5">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                          Entity Field {mapping.required && <span className="text-red-600">*</span>}
                        </label>
                        <input
                          type="text"
                          value={mapping.entityField}
                          onChange={(e) => handleMappingChange(index, 'entityField', e.target.value)}
                          placeholder="e.g., title, organization, deadline"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                        />
                      </div>

                      <div className="col-span-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveMapping(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <Button variant="outline" onClick={handleAddMapping} className="w-full">
                + Add Mapping
              </Button>

              {/* Advanced Options */}
              <div className="pt-4 border-t">
                <button
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {showAdvancedOptions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Advanced Options
                </button>

                {showAdvancedOptions && (
                  <div className="mt-3 space-y-3 pl-6">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={uploadOptions.strict}
                        onChange={(e) => setUploadOptions({ ...uploadOptions, strict: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      Strict validation (fail if required fields are missing)
                    </label>

                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={uploadOptions.skipInvalidRows}
                        onChange={(e) => setUploadOptions({ ...uploadOptions, skipInvalidRows: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      Skip invalid rows (import valid rows even if some fail)
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setStep('template')}>Back</Button>
                <Button onClick={handlePreview}>Next: Preview Data</Button>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && etlResult && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Preview & Validate</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Review the normalized data before importing
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{etlResult.stats.totalRows}</div>
                  <div className="text-xs text-blue-700 dark:text-blue-300">Total Rows</div>
                </Card>
                <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <div className="text-2xl font-bold text-green-900 dark:text-green-100">{etlResult.stats.validRows}</div>
                  <div className="text-xs text-green-700 dark:text-green-300">Valid Rows</div>
                </Card>
                <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <div className="text-2xl font-bold text-red-900 dark:text-red-100">{etlResult.stats.invalidRows}</div>
                  <div className="text-xs text-red-700 dark:text-red-300">Invalid Rows</div>
                </Card>
                <Card className="p-4 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{etlResult.stats.fieldsMatched}</div>
                  <div className="text-xs text-purple-700 dark:text-purple-300">Fields Mapped</div>
                </Card>
              </div>

              {/* Errors & Warnings */}
              {etlResult.errors.length > 0 && (
                <Card className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <div className="font-semibold text-red-900 dark:text-red-100 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Errors ({etlResult.errors.length})
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {etlResult.errors.slice(0, 10).map((err, idx) => (
                      <div key={idx} className="text-xs text-red-700 dark:text-red-300">
                        Row {err.row}, {err.field}: {err.message}
                      </div>
                    ))}
                    {etlResult.errors.length > 10 && (
                      <div className="text-xs text-red-600 dark:text-red-400 italic">
                        ... and {etlResult.errors.length - 10} more errors
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {etlResult.warnings.length > 0 && (
                <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <div className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                    Warnings ({etlResult.warnings.length})
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {etlResult.warnings.map((warn, idx) => (
                      <div key={idx} className="text-xs text-yellow-700 dark:text-yellow-300">{warn}</div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Preview Table */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Sample Data (first 5 rows)</h4>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Title</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Organization</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Deadline</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Location</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {etlResult.data.slice(0, 5).map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.title || '--'}</td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{row.organization || '--'}</td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                            {row.deadline ? new Date(row.deadline).toLocaleDateString() : '--'}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={row.status === 'open' ? 'default' : 'secondary'}>
                              {row.status || 'unknown'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{row.location || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setStep('mapping')}>Back</Button>
                <Button 
                  onClick={handleConfirmUpload} 
                  disabled={!etlResult.success}
                  className={etlResult.success ? '' : 'opacity-50'}
                >
                  {etlResult.success ? 'Import Opportunities' : 'Fix Errors to Continue'}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <div className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Processing Upload...</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Creating ingest job and normalizing data
              </div>
            </div>
          )}

          {/* Step: Complete */}
          {step === 'complete' && etlResult && (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Upload Complete!</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center max-w-md">
                Successfully imported {etlResult.stats.validRows} opportunities. 
                The data is now being processed and will appear in your opportunities list shortly.
              </div>
              <Button onClick={() => { reset(); onClose(); }}>Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

